/**
 * Self-contained toast system (no external dependency).
 *
 * A tiny Zustand store holds the active toasts; `toast.success(...)` etc. can be
 * called from anywhere (including non-React code like mutation handlers). The
 * <Toaster /> portal renders them in the corner with auto-dismiss.
 */
import React, { useEffect } from "react";
import { create } from "zustand";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import clsx from "clsx";

type ToastVariant = "success" | "error" | "info";

interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
  duration: number;
}

interface ToastState {
  toasts: ToastItem[];
  push: (t: Omit<ToastItem, "id">) => string;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}));

function show(variant: ToastVariant, message: string, duration = 4000) {
  return useToastStore.getState().push({ variant, message, duration });
}

/** Imperative API usable from React and plain functions alike. */
export const toast = {
  success: (message: string, duration?: number) =>
    show("success", message, duration),
  error: (message: string, duration?: number) =>
    show("error", message, duration ?? 6000),
  info: (message: string, duration?: number) => show("info", message, duration),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};

const VARIANT_STYLES: Record<
  ToastVariant,
  { icon: React.ReactNode; ring: string }
> = {
  success: {
    icon: <CheckCircle2 size={18} className="text-emerald-400" />,
    ring: "ring-emerald-500/30",
  },
  error: {
    icon: <AlertCircle size={18} className="text-red-400" />,
    ring: "ring-red-500/30",
  },
  info: {
    icon: <Info size={18} className="text-blue-400" />,
    ring: "ring-blue-500/30",
  },
};

const ToastRow: React.FC<{ item: ToastItem }> = ({ item }) => {
  const dismiss = useToastStore((s) => s.dismiss);
  useEffect(() => {
    const t = setTimeout(() => dismiss(item.id), item.duration);
    return () => clearTimeout(t);
  }, [item.id, item.duration, dismiss]);

  return (
    <div
      role="status"
      className={clsx(
        "animate-slideIn pointer-events-auto flex items-start gap-3 rounded-lg bg-slate-800 px-4 py-3 shadow-xl ring-1",
        VARIANT_STYLES[item.variant].ring,
      )}
    >
      <span className="mt-0.5 shrink-0">{VARIANT_STYLES[item.variant].icon}</span>
      <p className="flex-1 text-sm text-slate-100">{item.message}</p>
      <button
        onClick={() => dismiss(item.id)}
        className="shrink-0 text-slate-400 transition-colors hover:text-slate-200"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export const Toaster: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-[100] flex w-80 flex-col gap-3">
      {toasts.map((t) => (
        <ToastRow key={t.id} item={t} />
      ))}
    </div>
  );
};
